// /app/frontend/src/pages/ContactUsPage.jsx
/**
 * Contact Us Page
 * Provides email addresses with copy-to-clipboard functionality.
 * Avoids mailto: links that trigger Android's "Open with..." chooser (PayPal bug).
 */
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Copy, ShieldAlert, Phone } from "lucide-react";
import { toast } from "sonner";

const UNITYLOVE_SUPPORT_PHONE_DISPLAY = "(866) 260-6427";
const UNITYLOVE_SUPPORT_PHONE_TEL = "+18662606427";

export default function ContactUsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const mode = params.get("mode"); // "safety" optional

  const primaryEmail = "contact@joinunitylove.com";
  const safetyEmail = "safety@joinunitylove.com";

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      // fallback: old-school prompt
      window.prompt("Copy this email:", value);
    }
  };

  // Intentionally not the default action (avoids Android chooser weirdness like PayPal).
  const openEmailApp = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const EmailRow = ({ label, email, icon }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-gray-200 dark:border-slate-700">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
          <div className="font-semibold text-gray-900 dark:text-white truncate">{email}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          className="flex-1 sm:flex-none px-4 py-2 rounded-xl flex items-center justify-center gap-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium"
          onClick={() => copy(email)}
          type="button"
        >
          <Copy className="w-4 h-4" />
          Copy
        </button>

        <button
          className="flex-1 sm:flex-none px-4 py-2 rounded-xl flex items-center justify-center gap-2 bg-rose-600 text-white hover:bg-rose-700 transition-colors font-medium"
          onClick={() => openEmailApp(email)}
          type="button"
        >
          <Mail className="w-4 h-4" />
          Email
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            type="button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="text-sm text-gray-500 dark:text-gray-400">Support</div>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Contact UnityLove</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Copy the email (best on mobile), or tap "Email" to open your email app.
          </p>

          {/* Phone Support Card */}
          <div className="mb-6 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-5">
            <div className="text-sm text-gray-500 dark:text-gray-400">Phone Support</div>
            <a
              href={`tel:${UNITYLOVE_SUPPORT_PHONE_TEL}`}
              className="mt-1 block text-xl font-semibold text-gray-900 dark:text-white hover:text-rose-600 dark:hover:text-rose-400 hover:underline transition-colors"
            >
              <Phone className="w-5 h-5 inline-block mr-2" />
              {UNITYLOVE_SUPPORT_PHONE_DISPLAY}
            </a>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              If we miss your call, please leave a voicemail and our team will return your call as soon as possible.
            </p>
          </div>

          <div className="space-y-3">
            <EmailRow
              label="General support"
              email={primaryEmail}
              icon={<Mail className="w-5 h-5 text-rose-600" />}
            />

            <EmailRow
              label="Safety / reporting"
              email={safetyEmail}
              icon={<ShieldAlert className="w-5 h-5 text-amber-600" />}
            />
          </div>

          {mode === "safety" && (
            <div className="mt-6 rounded-2xl p-4 border border-amber-500/30 bg-amber-50 dark:bg-amber-500/10">
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Safety note</div>
              <div className="text-sm text-amber-700 dark:text-amber-400">
                If you're reporting a user, include the username, screenshots, and what happened.
              </div>
            </div>
          )}
        </div>

        {/* Additional info */}
        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>We typically respond within 24-48 hours</p>
        </div>
      </div>
    </div>
  );
}
